import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Paper,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api";

/** When false, hides “Full academic year (53 weeks) payment” on create and edit. Loaded values still submit on update. */
const SHOW_FULL_ACADEMIC_YEAR_FINANCE = false;

/** When false, hides 1/3/6/12‑month billing plan fields; API still receives zero amounts from existing FormData logic. */
const SHOW_COURSE_PLANS = false;

/** One schedule slot per mode × location × subject (matches backend `class_sessions`). */
function sessionScheduleKey(modeId, locationId, subjectId) {
  return `${Number(modeId)}-${Number(locationId)}-${Number(subjectId)}`;
}

function formatTimeForInput(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return s;
  const hh = String(Number(m[1])).padStart(2, "0");
  return `${hh}:${m[2]}`;
}

function modeLocationPlanKey(modeId, locationId) {
  return `${Number(modeId)}-${Number(locationId)}`;
}

export default function Course() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [fetchingCourse, setFetchingCourse] = useState(false);
  const [existingImage, setExistingImage] = useState(null);
  const [courseData, setCourseData] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const [academicYears, setAcademicYears] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [locations, setLocations] = useState([]);
  const [modes, setModes] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  const [weekDays, setWeekDays] = useState([]);

  const [features, setFeatures] = useState([]);
  const [featureInput, setFeatureInput] = useState("");

  const [onlineFeatures, setOnlineFeatures] = useState([]);
  const [onlineFeatureInput, setOnlineFeatureInput] = useState("");

  const [inPersonFeatures, setInPersonFeatures] = useState([]);
  const [inPersonFeatureInput, setInPersonFeatureInput] = useState("");

  const [subjects, setSubjects] = useState([]);
  /** Keys: `${modeId}-${locationId}-${subjectId}` → { held_time, duration_minutes, held_weekday_id } */
  const [classSessionSchedule, setClassSessionSchedule] = useState({});
  const legacySubjectDefaultsRef = useRef(null);
  const [locationsSelected, setLocationsSelected] = useState([]);

  const [onlineAmounts, setOnlineAmounts] = useState({ 1: "", 3: "", 6: "", 12: "" });
  const [inPersonAmounts, setInPersonAmounts] = useState({ 1: "", 3: "", 6: "", 12: "" });
  const [onlineRegistrationFees, setOnlineRegistrationFees] = useState({ 1: "", 3: "", 6: "", 12: "" });
  const [onlineNumberOfInstallments, setOnlineNumberOfInstallments] = useState({ 1: "", 3: "", 6: "", 12: "" });
  const [inPersonRegistrationFees, setInPersonRegistrationFees] = useState({ 1: "", 3: "", 6: "", 12: "" });
  const [inPersonNumberOfInstallments, setInPersonNumberOfInstallments] = useState({ 1: "", 3: "", 6: "", 12: "" });
  // Per-installment amounts per plan: { 1: ["100", "150"], 3: [], ... }
  const [onlineInstallmentAmounts, setOnlineInstallmentAmounts] = useState({ 1: [], 3: [], 6: [], 12: [] });
  const [inPersonInstallmentAmounts, setInPersonInstallmentAmounts] = useState({ 1: [], 3: [], 6: [], 12: [] });
  const [onlineInstallmentDueDates, setOnlineInstallmentDueDates] = useState({ 1: [], 3: [], 6: [], 12: [] });
  const [inPersonInstallmentDueDates, setInPersonInstallmentDueDates] = useState({ 1: [], 3: [], 6: [], 12: [] });
  const [planStartDates, setPlanStartDates] = useState({ 1: "", 3: "", 6: "", 12: "" });
  const [onlineRegOpeningDates, setOnlineRegOpeningDates] = useState({ 1: "", 3: "", 6: "", 12: "" });
  const [onlineRegClosingDates, setOnlineRegClosingDates] = useState({ 1: "", 3: "", 6: "", 12: "" });
  const [inPersonRegOpeningDates, setInPersonRegOpeningDates] = useState({ 1: "", 3: "", 6: "", 12: "" });
  const [inPersonRegClosingDates, setInPersonRegClosingDates] = useState({ 1: "", 3: "", 6: "", 12: "" });
  const [modeLocationPlans, setModeLocationPlans] = useState({});
  const [fullYearFinance, setFullYearFinance] = useState({
    registration_fee: "",
    total_course_fee: "",
    number_of_installments: "",
    installment_amounts: [],
    installment_due_dates: [],
  });

  const data = watch();
  const subscriptionPlans = [1, 3, 6, 12]; // months
  const selectedModeIds = (data.type_of_modes || [])
    .map((idVal) => Number(idVal))
    .filter((n) => Number.isFinite(n) && n > 0);
  const selectedLocationIds = (locationsSelected || [])
    .map((idVal) => Number(idVal))
    .filter((n) => Number.isFinite(n) && n > 0);
  const onlineLocationId =
    Number(
      locations.find((l) => String(l.name ?? "").trim().toLowerCase() === "online")?.id
    ) || 0;
  const isOnlineModeId = (modeId) => {
    const mode = modes.find((m) => Number(m.id) === Number(modeId));
    return !!mode && String(mode.name ?? "").toLowerCase().includes("online");
  };
  const isInPersonModeId = (modeId) => {
    const mode = modes.find((m) => Number(m.id) === Number(modeId));
    const name = String(mode?.name ?? "").toLowerCase();
    return name.includes("person");
  };
  const isVisibleModeLocationPlan = (modeId, locationId) => {
    const locIdNum = Number(locationId);
    if (isOnlineModeId(modeId)) {
      return onlineLocationId > 0 && locIdNum === Number(onlineLocationId);
    }
    if (isInPersonModeId(modeId)) {
      return !(onlineLocationId > 0 && locIdNum === Number(onlineLocationId));
    }
    return true;
  };
  const hasOnlineModeSelected = selectedModeIds.some((mid) => isOnlineModeId(mid));
  const hasInPersonModeSelected = selectedModeIds.some((mid) => isInPersonModeId(mid));
  const effectiveLocationIds = (() => {
    const base = [...selectedLocationIds];
    if (hasOnlineModeSelected && onlineLocationId > 0 && !base.includes(onlineLocationId)) {
      base.push(onlineLocationId);
    }
    return base;
  })();

  const createEmptySubjectSessions = () => {
    const map = {};
    (subjects || []).forEach((sidRaw) => {
      const sid = Number(sidRaw);
      if (!Number.isFinite(sid) || sid < 1) return;
      map[sid] = { held_time: "", duration_minutes: "", held_weekday_id: "" };
    });
    return map;
  };

  const createEmptyModeLocationPlan = () => ({
    plan_name: "",
    max_seats: "",
    course_plan_id: "",
    enrolled_count: null,
    available_seats: null,
    plan_start_date: "",
    plan_end_date: "",
    registration_fee: "",
    reg_opening_date: "",
    reg_closing_date: "",
    total_course_fee: "",
    number_of_installments: "",
    installment_amounts: [],
    installment_due_dates: [],
    subject_sessions: createEmptySubjectSessions(),
  });

  const normalizeSubjectSessions = (existingSessions = {}) => {
    if (!Array.isArray(subjects) || subjects.length < 1) {
      return existingSessions && typeof existingSessions === "object" ? { ...existingSessions } : {};
    }
    const next = {};
    (subjects || []).forEach((sidRaw) => {
      const sid = Number(sidRaw);
      if (!Number.isFinite(sid) || sid < 1) return;
      const current = existingSessions[sid] || existingSessions[String(sid)] || {};
      next[sid] = {
        held_time: current.held_time || "",
        duration_minutes:
          current.duration_minutes != null && current.duration_minutes !== ""
            ? String(current.duration_minutes)
            : "",
        held_weekday_id: current.held_weekday_id ?? "",
      };
    });
    return next;
  };

  useEffect(() => {
    const modeIds = (data.type_of_modes || [])
      .map((idVal) => Number(idVal))
      .filter((n) => Number.isFinite(n) && n > 0);
    const locationIds = effectiveLocationIds;

    if (modeIds.length < 1 || locationIds.length < 1) {
      setModeLocationPlans({});
      return;
    }

    setModeLocationPlans((prev) => {
      const next = {};
      modeIds.forEach((modeId) => {
        locationIds.forEach((locationId) => {
          if (!isVisibleModeLocationPlan(modeId, locationId)) return;
          const k = modeLocationPlanKey(modeId, locationId);
          const existing = prev[k];
          const plansArray = Array.isArray(existing)
            ? existing
            : existing
            ? [existing]
            : [createEmptyModeLocationPlan()];
          next[k] = plansArray.map((planObj) => ({
            ...createEmptyModeLocationPlan(),
            ...planObj,
            subject_sessions: normalizeSubjectSessions(planObj?.subject_sessions || {}),
          }));
        });
      });
      return next;
    });
  }, [data.type_of_modes, effectiveLocationIds.join(","), subjects, modes, locations]);

  // Ensure one schedule row exists per selected mode × location × subject; prune removed combinations.
  useEffect(() => {
    const modeIds = (data.type_of_modes || [])
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
    const locIds = locationsSelected
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
    const subIds = subjects
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);

    setClassSessionSchedule((prev) => {
      const next = { ...prev };
      const keys = new Set();
      const leg = legacySubjectDefaultsRef.current;

      modeIds.forEach((mid) => {
        locIds.forEach((lid) => {
          subIds.forEach((sid) => {
            const k = sessionScheduleKey(mid, lid, sid);
            keys.add(k);
            if (!next[k]) {
              const def =
                leg && leg[sid]
                  ? {
                      held_time: leg[sid].held_time || "",
                      duration_minutes: leg[sid].duration_minutes || "",
                      held_weekday_id: leg[sid].held_weekday_id ?? "",
                    }
                  : { held_time: "", duration_minutes: "", held_weekday_id: "" };
              next[k] = def;
            }
          });
        });
      });

      Object.keys(next).forEach((k) => {
        if (!keys.has(k)) delete next[k];
      });

      if (leg && keys.size > 0) {
        legacySubjectDefaultsRef.current = null;
      }
      return next;
    });
  }, [subjects, locationsSelected, data.type_of_modes]);

  // Fetch dropdown data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [years, schoolYrs, locs, modeList, subs, wds] = await Promise.all([
          api.get("common/data?param=AcdemicYears"),
          api.get("common/data?param=SchoolYears"),
          api.get("common/data?param=Locations"),
          api.get("common/data?param=Modes"),
          api.get("common/data?param=Subjects"),
          api.get("common/data?param=WeekDays"),
        ]);
        setAcademicYears(years.data.data);
        setSchoolYears(schoolYrs.data.data || []);
        setLocations(locs.data.data);
        setModes(modeList.data.data);
        setSubjectsList(subs.data.data);
        setWeekDays(wds.data.data || []);
      } catch (err) {
        console.error("Dropdown fetch error:", err);
        toast.error("Failed to load dropdown data, please try again.");
      }
    };
    fetchData();
  }, []);

  // Fetch course data for edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchCourseData = async () => {
        setFetchingCourse(true);
        try {
          const response = await api.get(`admin/assign/course/${id}`);
          if (response.data.success) {
            const courseData = response.data.data;
            
            // Store course data in state for later use (especially for academic year dropdown)
            setCourseData(courseData);
            
            // Transform API response to form format
            setValue("name", courseData.name || "");
            setValue("description", courseData.description || "");
            setValue("is_new", !!courseData.is_new);
            setValue("start_date", courseData.start_date || "");
            setValue("end_date", courseData.end_date || "");
            setValue("total_seats", courseData.total_seats ?? "");
            setValue("full_year_registration_fee", courseData.full_year_registration_fee ?? "");
            if (courseData.full_year_installment) {
              setValue("full_year_total_course_fee", courseData.full_year_installment.total_course_fee ?? "");
              setValue("full_year_number_of_installments", courseData.full_year_installment.number_of_installments ?? "");
              setValue(
                "full_year_reg_opening_date",
                courseData.full_year_installment.reg_opening_date ?? ""
              );
              setValue(
                "full_year_reg_closing_date",
                courseData.full_year_installment.reg_closing_date ?? ""
              );
              const items = Array.isArray(courseData.full_year_installment.installment_items)
                ? courseData.full_year_installment.installment_items
                : (courseData.full_year_installment.installmentItems || []);
              const amounts = Array.isArray(items)
                ? items
                    .map((it) => (it.amount != null ? String(it.amount) : ""))
                : [];
              const fyDueDates = Array.isArray(items)
                ? items
                    .sort((a, b) => (a.installment_number || 0) - (b.installment_number || 0))
                    .map((it) => (it.due_date || ""))
                : [];
              setFullYearFinance((prev) => ({
                ...prev,
                registration_fee: courseData.full_year_registration_fee != null ? String(courseData.full_year_registration_fee) : "",
                total_course_fee: courseData.full_year_installment.total_course_fee != null ? String(courseData.full_year_installment.total_course_fee) : "",
                number_of_installments: courseData.full_year_installment.number_of_installments != null ? String(courseData.full_year_installment.number_of_installments) : "",
                installment_amounts: amounts,
                installment_due_dates: fyDueDates,
              }));
            }
            // Note: acdemic_year_id will be set in a separate useEffect that waits for academicYears to load
            
            // Set multi-select fields
            if (courseData.subject_ids) {
              setSubjects(courseData.subject_ids);
            }
            legacySubjectDefaultsRef.current = null;
            if (Array.isArray(courseData.class_sessions) && courseData.class_sessions.length > 0) {
              const granular = courseData.class_sessions.filter(
                (r) => r && r.mode_id != null && r.location_id != null
              );
              if (granular.length > 0) {
                const scheduleMap = {};
                granular.forEach((row) => {
                  if (!row.subject_id) return;
                  const k = sessionScheduleKey(row.mode_id, row.location_id, row.subject_id);
                  scheduleMap[k] = {
                    held_time: formatTimeForInput(row.held_time),
                    duration_minutes:
                      row.duration_minutes != null && row.duration_minutes !== ""
                        ? String(row.duration_minutes)
                        : "",
                    held_weekday_id:
                      row.held_weekday_id != null && row.held_weekday_id !== ""
                        ? row.held_weekday_id
                        : "",
                  };
                });
                setClassSessionSchedule(scheduleMap);
              } else {
                const leg = {};
                courseData.class_sessions.forEach((row) => {
                  if (!row?.subject_id) return;
                  leg[row.subject_id] = {
                    held_time: formatTimeForInput(row.held_time),
                    duration_minutes:
                      row.duration_minutes != null && row.duration_minutes !== ""
                        ? String(row.duration_minutes)
                        : "",
                    held_weekday_id:
                      row.held_weekday_id != null && row.held_weekday_id !== ""
                        ? row.held_weekday_id
                        : "",
                  };
                });
                legacySubjectDefaultsRef.current = leg;
                setClassSessionSchedule({});
              }
            } else if (Array.isArray(courseData.subjects) && courseData.subjects.length > 0) {
              const leg = {};
              courseData.subjects.forEach((s) => {
                if (!s?.id) return;
                leg[s.id] = {
                  held_time: formatTimeForInput(s.held_time),
                  duration_minutes:
                    s.duration_minutes != null && s.duration_minutes !== ""
                      ? String(s.duration_minutes)
                      : "",
                  held_weekday_id:
                    s.held_weekday_id != null && s.held_weekday_id !== ""
                      ? s.held_weekday_id
                      : "",
                };
              });
              legacySubjectDefaultsRef.current = leg;
              setClassSessionSchedule({});
            } else if (Array.isArray(courseData.subject_ids) && courseData.subject_ids.length > 0) {
              const leg = {};
              courseData.subject_ids.forEach((sid) => {
                leg[sid] = { held_time: "", duration_minutes: "", held_weekday_id: "" };
              });
              legacySubjectDefaultsRef.current = leg;
              setClassSessionSchedule({});
            } else {
              setClassSessionSchedule({});
            }
            if (courseData.type_of_modes) {
              setValue("type_of_modes", courseData.type_of_modes);
            }
            if (courseData.location_ids) {
              setLocationsSelected(courseData.location_ids);
            }
            
            // Set features
            if (courseData.features_names) {
              setFeatures(courseData.features_names);
            }
            if (courseData.online_features_names) {
              setOnlineFeatures(courseData.online_features_names);
            }
            if (courseData.in_person_features_names) {
              setInPersonFeatures(courseData.in_person_features_names);
            }
            
            // Transform prices object to individual fields
            if (courseData.prices) {
              const onlinePrices = {};
              const inPersonPrices = {};
              const onlineRegFees = {};
              const onlineNumInstallments = {};
              const inPersonRegFees = {};
              const inPersonNumInstallments = {};
              const onlineInstAmounts = {};
              const inPersonInstAmounts = {};
              const onlineInstDueDates = {};
              const inPersonInstDueDates = {};
              const planStarts = {};
              const planEnds = {};
              const onlineRegOpens = {};
              const onlineRegCloses = {};
              const inPersonRegOpens = {};
              const inPersonRegCloses = {};

              Object.entries(courseData.prices).forEach(([key, value]) => {
                if (key.startsWith("amount_for_online_")) {
                  const period = key.replace("amount_for_online_", "");
                  onlinePrices[period] = value != null && value !== "" ? String(value) : "";
                } else if (key.startsWith("amount_for_in_person_")) {
                  const period = key.replace("amount_for_in_person_", "");
                  inPersonPrices[period] = value != null && value !== "" ? String(value) : "";
                } else if (key.startsWith("registration_fee_online_")) {
                  const period = key.replace("registration_fee_online_", "");
                  onlineRegFees[period] = value != null && value !== "" ? String(value) : "";
                } else if (key.startsWith("reg_opening_date_online_")) {
                  const period = key.replace("reg_opening_date_online_", "");
                  onlineRegOpens[period] = value != null && value !== "" ? String(value) : "";
                } else if (key.startsWith("reg_closing_date_online_")) {
                  const period = key.replace("reg_closing_date_online_", "");
                  onlineRegCloses[period] = value != null && value !== "" ? String(value) : "";
                } else if (key.startsWith("number_of_installments_online_")) {
                  const period = key.replace("number_of_installments_online_", "");
                  onlineNumInstallments[period] = value != null && value !== "" ? String(value) : "";
                } else if (key.startsWith("registration_fee_in_person_")) {
                  const period = key.replace("registration_fee_in_person_", "");
                  inPersonRegFees[period] = value != null && value !== "" ? String(value) : "";
                } else if (key.startsWith("reg_opening_date_in_person_")) {
                  const period = key.replace("reg_opening_date_in_person_", "");
                  inPersonRegOpens[period] = value != null && value !== "" ? String(value) : "";
                } else if (key.startsWith("reg_closing_date_in_person_")) {
                  const period = key.replace("reg_closing_date_in_person_", "");
                  inPersonRegCloses[period] = value != null && value !== "" ? String(value) : "";
                } else if (key.startsWith("number_of_installments_in_person_")) {
                  const period = key.replace("number_of_installments_in_person_", "");
                  inPersonNumInstallments[period] = value != null && value !== "" ? String(value) : "";
                } else if (key.startsWith("installment_amounts_online_")) {
                  const period = key.replace("installment_amounts_online_", "");
                  onlineInstAmounts[period] = Array.isArray(value) ? value.map((v) => String(v)) : [];
                } else if (key.startsWith("installment_due_dates_online_")) {
                  const period = key.replace("installment_due_dates_online_", "");
                  onlineInstDueDates[period] = Array.isArray(value) ? value.map((v) => (v || "")) : [];
                } else if (key.startsWith("installment_amounts_in_person_")) {
                  const period = key.replace("installment_amounts_in_person_", "");
                  inPersonInstAmounts[period] = Array.isArray(value) ? value.map((v) => String(v)) : [];
                } else if (key.startsWith("installment_due_dates_in_person_")) {
                  const period = key.replace("installment_due_dates_in_person_", "");
                  inPersonInstDueDates[period] = Array.isArray(value) ? value.map((v) => (v || "")) : [];
                } else if (key.startsWith("plan_start_date_")) {
                  const period = key.replace("plan_start_date_", "");
                  planStarts[period] = value != null && value !== "" ? String(value) : "";
                } else if (key.startsWith("plan_end_date_")) {
                  const period = key.replace("plan_end_date_", "");
                  planEnds[period] = value != null && value !== "" ? String(value) : "";
                }
              });

              setOnlineAmounts((prev) => ({ ...prev, ...onlinePrices }));
              setInPersonAmounts((prev) => ({ ...prev, ...inPersonPrices }));
              setOnlineRegistrationFees((prev) => ({ ...prev, ...onlineRegFees }));
              setOnlineNumberOfInstallments((prev) => ({ ...prev, ...onlineNumInstallments }));
              setInPersonRegistrationFees((prev) => ({ ...prev, ...inPersonRegFees }));
              setOnlineRegOpeningDates((prev) => ({ ...prev, ...onlineRegOpens }));
              setOnlineRegClosingDates((prev) => ({ ...prev, ...onlineRegCloses }));
              setInPersonRegOpeningDates((prev) => ({ ...prev, ...inPersonRegOpens }));
              setInPersonRegClosingDates((prev) => ({ ...prev, ...inPersonRegCloses }));
              setInPersonNumberOfInstallments((prev) => ({ ...prev, ...inPersonNumInstallments }));
              setOnlineInstallmentAmounts((prev) => ({ ...prev, ...onlineInstAmounts }));
              setInPersonInstallmentAmounts((prev) => ({ ...prev, ...inPersonInstAmounts }));
              setOnlineInstallmentDueDates((prev) => ({ ...prev, ...onlineInstDueDates }));
              setInPersonInstallmentDueDates((prev) => ({ ...prev, ...inPersonInstDueDates }));
              setPlanStartDates((prev) => ({ ...prev, ...planStarts }));

              // New UI: build one plan per mode × location (fallback from legacy mode-level prices).
              const allPeriods = subscriptionPlans;
              const pickLegacyByPriority = (amounts, regFees, nums, instAmounts, instDueDates, regOpens, regCloses, starts, ends) => {
                const selectedPeriod =
                  allPeriods.find((p) => parseFloat(amounts?.[p] || 0) > 0) ?? allPeriods[0];
                return {
                  plan_start_date: starts?.[selectedPeriod] || "",
                  plan_end_date: ends?.[selectedPeriod] || "",
                  registration_fee: regFees?.[selectedPeriod] || "",
                  reg_opening_date: regOpens?.[selectedPeriod] || "",
                  reg_closing_date: regCloses?.[selectedPeriod] || "",
                  total_course_fee: amounts?.[selectedPeriod] || "",
                  number_of_installments: nums?.[selectedPeriod] || "",
                  installment_amounts: Array.isArray(instAmounts?.[selectedPeriod]) ? instAmounts[selectedPeriod] : [],
                  installment_due_dates: Array.isArray(instDueDates?.[selectedPeriod]) ? instDueDates[selectedPeriod] : [],
                };
              };
              const onlineLegacy = pickLegacyByPriority(
                onlinePrices,
                onlineRegFees,
                onlineNumInstallments,
                onlineInstAmounts,
                onlineInstDueDates,
                onlineRegOpens,
                onlineRegCloses,
                planStarts,
                planEnds
              );
              const inPersonLegacy = pickLegacyByPriority(
                inPersonPrices,
                inPersonRegFees,
                inPersonNumInstallments,
                inPersonInstAmounts,
                inPersonInstDueDates,
                inPersonRegOpens,
                inPersonRegCloses,
                planStarts,
                planEnds
              );
              const modeNameById = {};
              (courseData.class_sessions || []).forEach((s) => {
                if (s?.mode_id != null && s?.mode_name) {
                  modeNameById[Number(s.mode_id)] = String(s.mode_name).toLowerCase();
                }
              });
              const seededPlans = {};
              (courseData.type_of_modes || []).forEach((midRaw) => {
                const mid = Number(midRaw);
                const modeName = modeNameById[mid] || "";
                const defaults = modeName.includes("online") ? onlineLegacy : inPersonLegacy;
                (courseData.location_ids || []).forEach((lidRaw) => {
                  const lid = Number(lidRaw);
                  seededPlans[modeLocationPlanKey(mid, lid)] = [
                    {
                      ...createEmptyModeLocationPlan(),
                      ...defaults,
                      subject_sessions: normalizeSubjectSessions({}),
                    },
                  ];
                });
              });
              setModeLocationPlans((prev) => ({ ...prev, ...seededPlans }));
            }

            if (Array.isArray(courseData.mode_location_plans) && courseData.mode_location_plans.length > 0) {
              const serverPlans = {};
              courseData.mode_location_plans.forEach((row) => {
                const mid = Number(row?.mode_id);
                const lid = Number(row?.location_id);
                if (!Number.isFinite(mid) || !Number.isFinite(lid)) return;
                const key = modeLocationPlanKey(mid, lid);
                if (!Array.isArray(serverPlans[key])) serverPlans[key] = [];
                serverPlans[key].push({
                  plan_name: row.plan_name || row.course_plan?.name || "",
                  max_seats: row.max_seats != null && row.max_seats !== ""
                    ? String(row.max_seats)
                    : row.course_plan?.max_seats != null
                    ? String(row.course_plan.max_seats)
                    : courseData.total_seats != null
                    ? String(courseData.total_seats)
                    : "",
                  course_plan_id: row.course_plan_id ? String(row.course_plan_id) : "",
                  enrolled_count: row.enrolled_count ?? null,
                  available_seats: row.available_seats ?? null,
                  plan_start_date: row.plan_start_date || "",
                  plan_end_date: row.plan_end_date || "",
                  registration_fee: row.registration_fee != null ? String(row.registration_fee) : "",
                  reg_opening_date: row.reg_opening_date || "",
                  reg_closing_date: row.reg_closing_date || "",
                  total_course_fee: row.total_course_fee != null ? String(row.total_course_fee) : "",
                  number_of_installments: row.number_of_installments != null ? String(row.number_of_installments) : "",
                  installment_amounts: Array.isArray(row.installment_amounts)
                    ? row.installment_amounts.map((v) => String(v ?? ""))
                    : [],
                  installment_due_dates: Array.isArray(row.installment_due_dates)
                    ? row.installment_due_dates.map((v) => v || "")
                    : [],
                  subject_sessions:
                    row.subject_sessions && typeof row.subject_sessions === "object"
                      ? row.subject_sessions
                      : {},
                });
              });
              setModeLocationPlans((prev) => ({ ...prev, ...serverPlans }));
            }
            
            // Set existing image
            if (courseData.image) {
              setExistingImage(courseData.image);
            }
          }
        } catch (err) {
          console.error("Error fetching course:", err);
          if (err.response?.status === 404) {
            toast.error("Course not found");
            navigate("/admin/course-list");
          } else {
            toast.error("Failed to load course data");
          }
        } finally {
          setFetchingCourse(false);
        }
      };
      fetchCourseData();
    }
  }, [id, isEditMode, setValue, navigate]);

  // Set academic year value when both courseData and academicYears are available
  useEffect(() => {
    if (isEditMode && courseData && academicYears.length > 0) {
      const apiYearId = courseData.acdemic_year_id;
      console.log('🔵 Academic Year useEffect triggered');
      console.log('🔵 API Year ID:', apiYearId, 'Type:', typeof apiYearId);
      console.log('🔵 Available academic years:', academicYears);
      
      if (!apiYearId && apiYearId !== 0) {
        console.warn('⚠️ No academic year ID in course data');
        return;
      }

      // Check the type of IDs in the dropdown to ensure type match
      const firstYearId = academicYears[0]?.id;
      const isNumberType = typeof firstYearId === 'number';
      console.log('🔵 First year ID type:', typeof firstYearId, 'Value:', firstYearId);
      
      // Convert to match the dropdown type
      const yearId = isNumberType ? Number(apiYearId) : String(apiYearId);
      console.log('🔵 Converted year ID:', yearId, 'Type:', typeof yearId);
      
      // Verify the year exists in the dropdown options
      const yearExists = academicYears.some(y => {
        // Compare with type coercion to handle both number and string
        const matches = y.id == yearId || String(y.id) === String(yearId) || Number(y.id) === Number(yearId);
        if (matches) {
          console.log('✅ Found matching year:', y);
        }
        return matches;
      });
      
      if (yearExists) {
        console.log('✅ Setting academic year ID:', yearId, 'Type:', typeof yearId);
        setValue("acdemic_year_id", yearId, { shouldValidate: false, shouldDirty: true });
        // Verify the value was set
        setTimeout(() => {
          const currentValue = watch("acdemic_year_id");
          console.log('🔵 Current form value after setValue:', currentValue);
          if (currentValue !== yearId) {
            console.warn('⚠️ Value mismatch! Expected:', yearId, 'Got:', currentValue);
          }
        }, 100);
      } else {
        console.error('❌ Academic year ID not found in dropdown:', yearId, 'Available years:', academicYears.map(y => ({ id: y.id, type: typeof y.id, name: y.start_end_year })));
      }
    }
  }, [academicYears, courseData, isEditMode, setValue]);

  useEffect(() => {
    if (!isEditMode || !courseData || schoolYears.length === 0) return;
    const sid = courseData.school_year_id;
    if (sid == null && sid !== 0) return;
    const firstId = schoolYears[0]?.id;
    const idVal = typeof firstId === "number" ? Number(sid) : String(sid);
    const exists = schoolYears.some((y) => String(y.id) === String(idVal));
    if (exists) {
      setValue("school_year_id", idVal, { shouldValidate: false, shouldDirty: true });
    }
  }, [schoolYears, courseData, isEditMode, setValue]);

  // ---------- Feature Handlers ----------
  const handleAddFeature = () => {
    if (featureInput.trim().length < 50) {
      toast.error("Feature must be at least 50 characters.");
      return;
    }
    setFeatures([...features, featureInput.trim()]);
    setFeatureInput("");
  };

  const handleAddOnlineFeature = () => {
    if (onlineFeatureInput.trim().length < 50) {
      toast.error("Feature must be at least 50 characters.");
      return;
    }
    setOnlineFeatures([...onlineFeatures, onlineFeatureInput.trim()]);
    setOnlineFeatureInput("");
  };

  const handleAddInPersonFeature = () => {
    if (inPersonFeatureInput.trim().length < 50) {
      toast.error("Feature must be at least 50 characters.");
      return;
    }
    setInPersonFeatures([...inPersonFeatures, inPersonFeatureInput.trim()]);
    setInPersonFeatureInput("");
  };

  // ---------- Select Handlers ----------
  const handleAddSubject = (id) => {
    if (!subjects.includes(id)) {
      setSubjects([...subjects, id]);
    }
  };
  const handleAddLocation = (id) => {
    if (!locationsSelected.includes(id))
      setLocationsSelected([...locationsSelected, id]);
  };

  // Check if a specific mode is selected
  const isModeSelected = (modeName) => {
    return data.type_of_modes?.some(modeId => {
      const mode = modes.find(m => m.id === Number(modeId));
      return mode && mode.name.toLowerCase().includes(modeName.toLowerCase());
    });
  };

  // Get mode name by ID
  const getModeName = (modeId) => {
    const mode = modes.find(m => m.id === Number(modeId));
    return mode ? mode.name : "";
  };

  // When Online mode is selected, default-select the "online" location (matches backend locations seed).
  useEffect(() => {
    if (!locations.length || !modes.length) return;

    const onlineLocation = locations.find(
      (l) => String(l.name ?? "").trim().toLowerCase() === "online"
    );
    if (!onlineLocation?.id) return;

    const onlineModeOn = (data.type_of_modes || []).some((modeId) => {
      const mode = modes.find((m) => m.id === Number(modeId));
      return mode && mode.name.toLowerCase().includes("online");
    });

    const onlineLocId = onlineLocation.id;
    setLocationsSelected((prev) => {
      const has = prev.some((x) => Number(x) === Number(onlineLocId));
      if (onlineModeOn && !has) return [...prev, onlineLocId];
      if (!onlineModeOn && has) return prev.filter((x) => Number(x) !== Number(onlineLocId));
      return prev;
    });
  }, [data.type_of_modes, locations, modes]);

  // ---------- Submit ----------
  const onSubmit = async (formValues) => {
    setLoading(true);
    try {
      if (!formValues.total_seats || parseInt(formValues.total_seats, 10) < 1) {
        toast.error("Please enter total seats.");
        setLoading(false);
        return;
      }
      // Validate features
      if (features.length === 0) {
        toast.error("Please provide at least one feature");
        setLoading(false);
        return;
      }

      // Validate per mode × location × subject schedule (class_sessions)
      const modeIdsForSchedule = (formValues.type_of_modes || data.type_of_modes || [])
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n) && n > 0);
      const locIdsForSchedule = effectiveLocationIds;
      if (modeIdsForSchedule.length < 1) {
        toast.error("Please select at least one mode.");
        setLoading(false);
        return;
      }
      if (locIdsForSchedule.length < 1) {
        toast.error("Please select at least one location.");
        setLoading(false);
        return;
      }
      if (subjects.length < 1) {
        toast.error("Please select at least one subject.");
        setLoading(false);
        return;
      }

      const visiblePlanDates = [];
      modeIdsForSchedule.forEach((mid) => {
        locIdsForSchedule.forEach((lid) => {
          if (!isVisibleModeLocationPlan(mid, lid)) return;
          const k = modeLocationPlanKey(mid, lid);
          const plans = Array.isArray(modeLocationPlans[k])
            ? modeLocationPlans[k]
            : modeLocationPlans[k]
            ? [modeLocationPlans[k]]
            : [];
          plans.forEach((p) => {
            if (p?.plan_start_date && p?.plan_end_date) {
              visiblePlanDates.push({
                start: p.plan_start_date,
                end: p.plan_end_date,
              });
            }
          });
        });
      });
      if (visiblePlanDates.length < 1) {
        toast.error("Please add at least one plan with start and end dates.");
        setLoading(false);
        return;
      }
      const courseStartDate = visiblePlanDates
        .map((d) => d.start)
        .sort((a, b) => new Date(a) - new Date(b))[0];
      const courseEndDate = visiblePlanDates
        .map((d) => d.end)
        .sort((a, b) => new Date(b) - new Date(a))[0];
      for (const mid of modeIdsForSchedule) {
        for (const lid of locIdsForSchedule) {
          if (!isVisibleModeLocationPlan(mid, lid)) continue;
          const k = modeLocationPlanKey(mid, lid);
          const plans = Array.isArray(modeLocationPlans[k])
            ? modeLocationPlans[k]
            : modeLocationPlans[k]
            ? [modeLocationPlans[k]]
            : [];
          const modeName = getModeName(mid) || "Mode";
          const locationName = locations.find((l) => Number(l.id) === Number(lid))?.name || "Location";
          if (plans.length < 1) {
            toast.error(`${modeName} - ${locationName}: add at least one plan.`);
            setLoading(false);
            return;
          }
          for (let planIdx = 0; planIdx < plans.length; planIdx += 1) {
            const plan = plans[planIdx] || {};
            const total = parseFloat(plan.total_course_fee);
            const num = parseInt(plan.number_of_installments, 10);
            const prefix = `${modeName} - ${locationName} (Plan ${planIdx + 1})`;

            if (!String(plan.plan_name || "").trim()) {
              toast.error(`${prefix}: please enter a group name.`);
              setLoading(false);
              return;
            }
            const maxSeats = parseInt(plan.max_seats, 10);
            if (!Number.isFinite(maxSeats) || maxSeats < 1) {
              toast.error(`${prefix}: please enter max seats for this group (at least 1).`);
              setLoading(false);
              return;
            }
            if (!plan.plan_start_date) {
              toast.error(`${prefix}: please select plan start date.`);
              setLoading(false);
              return;
            }
            if (!plan.plan_end_date) {
              toast.error(`${prefix}: please select plan end date.`);
              setLoading(false);
              return;
            }
            if (plan.plan_end_date < plan.plan_start_date) {
              toast.error(`${prefix}: plan end date must be after start date.`);
              setLoading(false);
              return;
            }
            if (!plan.reg_opening_date) {
              toast.error(`${prefix}: please select registration start date.`);
              setLoading(false);
              return;
            }
            if (!plan.reg_closing_date) {
              toast.error(`${prefix}: please select registration end date.`);
              setLoading(false);
              return;
            }
            if (plan.reg_closing_date < plan.reg_opening_date) {
              toast.error(`${prefix}: registration end date must be after start date.`);
              setLoading(false);
              return;
            }
            if (!Number.isFinite(total) || total <= 0) {
              toast.error(`${prefix}: please enter a valid course fee total.`);
              setLoading(false);
              return;
            }
            if (!Number.isFinite(num) || num < 1) {
              toast.error(`${prefix}: please enter number of installments.`);
              setLoading(false);
              return;
            }

            const amounts = Array.isArray(plan.installment_amounts) ? plan.installment_amounts : [];
            const dueDates = Array.isArray(plan.installment_due_dates) ? plan.installment_due_dates : [];
            if (amounts.length !== num || dueDates.length !== num) {
              toast.error(`${prefix}: fill every installment amount and due date.`);
              setLoading(false);
              return;
            }
            if (dueDates.some((d) => !String(d || "").trim())) {
              toast.error(`${prefix}: every installment must include due date.`);
              setLoading(false);
              return;
            }
            for (const sidRaw of subjects) {
              const sid = Number(sidRaw);
              if (!Number.isFinite(sid) || sid < 1) continue;
              const sched = plan.subject_sessions?.[sid] || plan.subject_sessions?.[String(sid)] || {};
              if (!String(sched.held_time || "").trim()) {
                toast.error(`${prefix}: please provide subject time for all selected subjects.`);
                setLoading(false);
                return;
              }
              const duration = parseInt(sched.duration_minutes, 10);
              if (!Number.isFinite(duration) || duration < 1) {
                toast.error(`${prefix}: please provide valid subject duration for all selected subjects.`);
                setLoading(false);
                return;
              }
              if (sched.held_weekday_id == null || sched.held_weekday_id === "") {
                toast.error(`${prefix}: please select subject weekday for all selected subjects.`);
                setLoading(false);
                return;
              }
            }
          }
        }
      }

      // Validate full-year finance if any field provided (section only shown when editing)
      const fyReg = (formValues.full_year_registration_fee ?? "").toString().trim();
      const fyTotal = (formValues.full_year_total_course_fee ?? "").toString().trim();
      const fyNum = (formValues.full_year_number_of_installments ?? "").toString().trim();
      const fyAny = fyReg !== "" || fyTotal !== "" || fyNum !== "" || (fullYearFinance.installment_amounts || []).some((v) => String(v || "").trim() !== "");
      if (SHOW_FULL_ACADEMIC_YEAR_FINANCE && isEditMode && fyAny) {
        if (!fyTotal || parseFloat(fyTotal) <= 0) {
          toast.error("Please enter full-year total course fee.");
          setLoading(false);
          return;
        }
        const n = parseInt(fyNum, 10);
        if (!Number.isFinite(n) || n < 1) {
          toast.error("Please enter full-year number of installments.");
          setLoading(false);
          return;
        }

        const fyRegOpening = (formValues.full_year_reg_opening_date ?? "").toString().trim();
        const fyRegClosing = (formValues.full_year_reg_closing_date ?? "").toString().trim();

        if (!fyRegOpening) {
          toast.error("Please select registration start date for full academic year.");
          setLoading(false);
          return;
        }
        if (!fyRegClosing) {
          toast.error("Please select registration end date for full academic year.");
          setLoading(false);
          return;
        }
        if (fyRegOpening && fyRegClosing && new Date(fyRegClosing) < new Date(fyRegOpening)) {
          toast.error("Full academic year registration end date must be after the start date.");
          setLoading(false);
          return;
        }
      }

      // Validate course image (only required for create, optional for edit)
      if (!isEditMode && !(formValues.course_image instanceof File)) {
        toast.error("Please select a valid image file.");
        setLoading(false);
        return;
      }

      const formData = new FormData();

      // Append basic form values
      formData.append("name", formValues.name);
      formData.append("acdemic_year_id", formValues.acdemic_year_id);
      formData.append("school_year_id", formValues.school_year_id);
      formData.append("is_new", formValues.is_new ? "1" : "0");
      formData.append("description", formValues.description);
      formData.append("total_seats", formValues.total_seats);
      formData.append("start_date", courseStartDate);
      formData.append("end_date", courseEndDate);

      // Full academic year (53 weeks) finance — only when editing (hidden on create)
      if (isEditMode) {
        if (formValues.full_year_registration_fee != null && formValues.full_year_registration_fee !== "") {
          formData.append("full_year_registration_fee", formValues.full_year_registration_fee);
        }
        if (formValues.full_year_total_course_fee != null && formValues.full_year_total_course_fee !== "") {
          formData.append("full_year_total_course_fee", formValues.full_year_total_course_fee);
        }
        if (formValues.full_year_number_of_installments != null && formValues.full_year_number_of_installments !== "") {
          formData.append("full_year_number_of_installments", formValues.full_year_number_of_installments);
        }

        if (formValues.full_year_reg_opening_date != null && formValues.full_year_reg_opening_date !== "") {
          formData.append("full_year_reg_opening_date", formValues.full_year_reg_opening_date);
        }
        if (formValues.full_year_reg_closing_date != null && formValues.full_year_reg_closing_date !== "") {
          formData.append("full_year_reg_closing_date", formValues.full_year_reg_closing_date);
        }
        const fyInstAmounts = fullYearFinance.installment_amounts || [];
        fyInstAmounts.forEach((amt) => {
          if (amt != null && String(amt).trim() !== "") {
            formData.append("full_year_installment_amounts[]", amt);
          }
        });

        const fyInstDueDates = fullYearFinance.installment_due_dates || [];
        fyInstDueDates.forEach((date) => {
          formData.append("full_year_installment_due_dates[]", date || "");
        });
      }
      
      // Only append image if it's a new file (for create or update with new image)
      if (formValues.course_image instanceof File) {
        formData.append("course_image", formValues.course_image);
      }

      // Append multi-selects using [] format as per API documentation
      if (formValues.type_of_modes) {
        formValues.type_of_modes.forEach((val) => {
          formData.append('type_of_modes[]', val);
        });
      }

      subjects.forEach((val) => {
        formData.append('subject_ids[]', val);
      });

      let sessionIdx = 0;
      const getFallbackScheduleForMode = (modeId, subjectId) => {
        const modePlans = Object.entries(modeLocationPlans)
          .filter(([key, value]) => {
            const [mid] = String(key).split("-").map(Number);
            return Number(mid) === Number(modeId) && Array.isArray(value) && value.length > 0;
          })
          .flatMap(([, plans]) => plans);
        for (const p of modePlans) {
          const s = p?.subject_sessions?.[subjectId] || p?.subject_sessions?.[String(subjectId)];
          if (s && s.held_time && s.duration_minutes && s.held_weekday_id) return s;
        }
        return null;
      };
      (formValues.type_of_modes || []).forEach((midRaw) => {
        const mid = Number(midRaw);
        if (!Number.isFinite(mid) || mid < 1) return;
        effectiveLocationIds.forEach((lidRaw) => {
          const lid = Number(lidRaw);
          if (!Number.isFinite(lid) || lid < 1) return;
          const key = modeLocationPlanKey(mid, lid);
          const firstPlan = Array.isArray(modeLocationPlans[key])
            ? modeLocationPlans[key][0]
            : modeLocationPlans[key];
          subjects.forEach((sidRaw) => {
            const sid = Number(sidRaw);
            if (!Number.isFinite(sid) || sid < 1) return;
            const fallbackSched = getFallbackScheduleForMode(mid, sid);
            const sched =
              firstPlan?.subject_sessions?.[sid] ||
              firstPlan?.subject_sessions?.[String(sid)] ||
              fallbackSched ||
              classSessionSchedule[sessionScheduleKey(mid, lid, sid)] ||
              {};
            formData.append(`class_sessions[${sessionIdx}][subject_id]`, String(sid));
            formData.append(`class_sessions[${sessionIdx}][mode_id]`, String(mid));
            formData.append(`class_sessions[${sessionIdx}][location_id]`, String(lid));
            formData.append(`class_sessions[${sessionIdx}][held_time]`, sched.held_time || "");
            formData.append(`class_sessions[${sessionIdx}][duration_minutes]`, sched.duration_minutes || "");
            formData.append(
              `class_sessions[${sessionIdx}][held_weekday_id]`,
              sched.held_weekday_id != null && sched.held_weekday_id !== "" ? String(sched.held_weekday_id) : ""
            );
            sessionIdx += 1;
          });
        });
      });

      // New payload: multiple plans per mode × location.
      let planIdx = 0;
      (formValues.type_of_modes || []).forEach((midRaw) => {
        const mid = Number(midRaw);
        if (!Number.isFinite(mid) || mid < 1) return;
        effectiveLocationIds.forEach((lidRaw) => {
          const lid = Number(lidRaw);
          if (!Number.isFinite(lid) || lid < 1) return;
          if (!isVisibleModeLocationPlan(mid, lid)) return;
          const key = modeLocationPlanKey(mid, lid);
          const plans = Array.isArray(modeLocationPlans[key])
            ? modeLocationPlans[key]
            : modeLocationPlans[key]
            ? [modeLocationPlans[key]]
            : [];
          plans.forEach((plan, localPlanIdx) => {
            formData.append(`mode_location_plans[${planIdx}][mode_id]`, String(mid));
            formData.append(`mode_location_plans[${planIdx}][location_id]`, String(lid));
            formData.append(`mode_location_plans[${planIdx}][plan_index]`, String(localPlanIdx));
            formData.append(`mode_location_plans[${planIdx}][plan_name]`, String(plan.plan_name || "").trim());
            formData.append(`mode_location_plans[${planIdx}][max_seats]`, String(plan.max_seats || ""));
            formData.append(`mode_location_plans[${planIdx}][plan_start_date]`, plan.plan_start_date || "");
            formData.append(`mode_location_plans[${planIdx}][plan_end_date]`, plan.plan_end_date || "");
            if (plan.plan_start_date && plan.plan_end_date) {
              const start = new Date(plan.plan_start_date);
              const end = new Date(plan.plan_end_date);
              const days = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
              formData.append(
                `mode_location_plans[${planIdx}][course_duration_days]`,
                Number.isFinite(days) && days > 0 ? String(days) : ""
              );
            } else {
              formData.append(`mode_location_plans[${planIdx}][course_duration_days]`, "");
            }
            formData.append(`mode_location_plans[${planIdx}][registration_fee]`, plan.registration_fee || "");
            formData.append(`mode_location_plans[${planIdx}][reg_opening_date]`, plan.reg_opening_date || "");
            formData.append(`mode_location_plans[${planIdx}][reg_closing_date]`, plan.reg_closing_date || "");
            formData.append(`mode_location_plans[${planIdx}][total_course_fee]`, plan.total_course_fee || "");
            formData.append(
              `mode_location_plans[${planIdx}][number_of_installments]`,
              plan.number_of_installments || ""
            );
            (plan.installment_amounts || []).forEach((v) =>
              formData.append(`mode_location_plans[${planIdx}][installment_amounts][]`, v)
            );
            (plan.installment_due_dates || []).forEach((v) =>
              formData.append(`mode_location_plans[${planIdx}][installment_due_dates][]`, v || "")
            );
            subjects.forEach((sidRaw) => {
              const sid = Number(sidRaw);
              if (!Number.isFinite(sid) || sid < 1) return;
              const sched = plan.subject_sessions?.[sid] || plan.subject_sessions?.[String(sid)] || {};
              formData.append(`mode_location_plans[${planIdx}][subject_sessions][${sid}][subject_id]`, String(sid));
              formData.append(
                `mode_location_plans[${planIdx}][subject_sessions][${sid}][held_weekday_id]`,
                sched.held_weekday_id != null && sched.held_weekday_id !== "" ? String(sched.held_weekday_id) : ""
              );
              formData.append(
                `mode_location_plans[${planIdx}][subject_sessions][${sid}][held_time]`,
                sched.held_time || ""
              );
              formData.append(
                `mode_location_plans[${planIdx}][subject_sessions][${sid}][duration_minutes]`,
                sched.duration_minutes || ""
              );
            });
            planIdx += 1;
          });
        });
      });

      effectiveLocationIds.forEach((val) => {
        formData.append('location_ids[]', val);
      });

      // Append features using [] format as per API documentation
      features.forEach((f) => {
        formData.append('features_names[]', f);
      });

      onlineFeatures.forEach((f) => {
        formData.append('online_features_names[]', f);
      });

      inPersonFeatures.forEach((f) => {
        formData.append('in_person_features_names[]', f);
      });

      // Helper: get N installment amounts (use existing if valid, else equal split)
      const getInstallmentAmounts = (total, num, existing) => {
        const totalNum = parseFloat(total);
        if (Array.isArray(existing) && existing.length === num) {
          const parsed = existing.map((v) => parseFloat(v) || 0);
          const sum = parsed.reduce((s, v) => s + v, 0);
          // Compatibility payload to legacy backend still expects sum == total.
          // Keep user-entered unequal installments in mode_location_plans payload.
          if (Number.isFinite(totalNum) && Math.abs(sum - totalNum) <= 0.01) return parsed;
        }
        if (!num || num < 1 || !Number.isFinite(totalNum) || totalNum <= 0) return [];
        const per = (totalNum / num).toFixed(2);
        return Array(num)
          .fill(per)
          .map((v, i) => (i === num - 1 ? (totalNum - (num - 1) * parseFloat(per)).toFixed(2) : v))
          .map(Number);
      };

      // Compatibility payload for current backend validators:
      // send one mode-level plan as 1-month values (picked from first location plan for each mode).
      const firstPlanByMode = {};
      (formValues.type_of_modes || []).forEach((midRaw) => {
        const mid = Number(midRaw);
        if (!Number.isFinite(mid) || mid < 1 || firstPlanByMode[mid]) return;
        const firstLoc = effectiveLocationIds.find((lidRaw) =>
          isVisibleModeLocationPlan(mid, Number(lidRaw)) &&
          modeLocationPlans[modeLocationPlanKey(mid, Number(lidRaw))]
        );
        if (!firstLoc) return;
        const raw = modeLocationPlans[modeLocationPlanKey(mid, Number(firstLoc))];
        firstPlanByMode[mid] = Array.isArray(raw) ? raw[0] : raw;
      });
      const onlineModeIds = (formValues.type_of_modes || [])
        .map((midRaw) => Number(midRaw))
        .filter((mid) => getModeName(mid).toLowerCase().includes("online"));
      const inPersonModeIds = (formValues.type_of_modes || [])
        .map((midRaw) => Number(midRaw))
        .filter((mid) => getModeName(mid).toLowerCase().includes("person"));
      const onlineCompat = onlineModeIds.length > 0 ? firstPlanByMode[onlineModeIds[0]] : null;
      const inPersonCompat = inPersonModeIds.length > 0 ? firstPlanByMode[inPersonModeIds[0]] : null;

      if (onlineCompat) {
        const num = parseInt(onlineCompat.number_of_installments, 10) || 0;
        formData.append("plan_start_dates[1]", onlineCompat.plan_start_date || "");
        formData.append("reg_opening_dates_online[1]", onlineCompat.reg_opening_date || "");
        formData.append("reg_closing_dates_online[1]", onlineCompat.reg_closing_date || "");
        formData.append("amount_for_online_1", parseFloat(onlineCompat.total_course_fee || 0).toFixed(2));
        if (onlineCompat.registration_fee !== "") {
          formData.append("registration_fee_online_1", parseFloat(onlineCompat.registration_fee || 0).toFixed(2));
        }
        if (num > 0) {
          formData.append("number_of_installments_online_1", num);
          const amounts = getInstallmentAmounts(
            onlineCompat.total_course_fee,
            num,
            onlineCompat.installment_amounts
          );
          amounts.forEach((v) => formData.append("installment_amounts_online_1[]", v));
          (onlineCompat.installment_due_dates || []).forEach((d) =>
            formData.append("installment_due_dates_online_1[]", d || "")
          );
        }
      }

      if (inPersonCompat) {
        const num = parseInt(inPersonCompat.number_of_installments, 10) || 0;
        formData.append("plan_start_dates[1]", inPersonCompat.plan_start_date || "");
        formData.append("reg_opening_dates_in_person[1]", inPersonCompat.reg_opening_date || "");
        formData.append("reg_closing_dates_in_person[1]", inPersonCompat.reg_closing_date || "");
        formData.append("amount_for_in_person_1", parseFloat(inPersonCompat.total_course_fee || 0).toFixed(2));
        if (inPersonCompat.registration_fee !== "") {
          formData.append(
            "registration_fee_in_person_1",
            parseFloat(inPersonCompat.registration_fee || 0).toFixed(2)
          );
        }
        if (num > 0) {
          formData.append("number_of_installments_in_person_1", num);
          const amounts = getInstallmentAmounts(
            inPersonCompat.total_course_fee,
            num,
            inPersonCompat.installment_amounts
          );
          amounts.forEach((v) => formData.append("installment_amounts_in_person_1[]", v));
          (inPersonCompat.installment_due_dates || []).forEach((d) =>
            formData.append("installment_due_dates_in_person_1[]", d || "")
          );
        }
      }

      // Determine API endpoint and method
      const endpoint = isEditMode 
        ? `admin/assign/course/${id}` 
        : "admin/assign/course";
      
      // For edit mode with FormData, use POST with _method=PUT
      // This handles file uploads correctly in PHP/Laravel backends
      let method = "post";
      
      if (isEditMode) {
        formData.append('_method', 'PUT');
      }

      // Don't set Content-Type for FormData - axios will set it automatically with boundary
      const response = await api[method](endpoint, formData);

      if (response.data.success) {
        toast.success(isEditMode ? "Course updated successfully!" : "Course created successfully!");
        
        if (isEditMode) {
          navigate(`/admin/course/${id}`);
        } else {
          // Reset form for create mode
          reset();
          setFeatures([]);
          setOnlineFeatures([]);
          setInPersonFeatures([]);
          setSubjects([]);
          legacySubjectDefaultsRef.current = null;
          setClassSessionSchedule({});
          setLocationsSelected([]);
          setOnlineAmounts({ 1: "", 3: "", 6: "", 12: "" });
          setInPersonAmounts({ 1: "", 3: "", 6: "", 12: "" });
          setOnlineRegistrationFees({ 1: "", 3: "", 6: "", 12: "" });
          setOnlineNumberOfInstallments({ 1: "", 3: "", 6: "", 12: "" });
          setInPersonRegistrationFees({ 1: "", 3: "", 6: "", 12: "" });
          setInPersonNumberOfInstallments({ 1: "", 3: "", 6: "", 12: "" });
          setOnlineInstallmentAmounts({ 1: [], 3: [], 6: [], 12: [] });
          setInPersonInstallmentAmounts({ 1: [], 3: [], 6: [], 12: [] });
          setPlanStartDates({ 1: "", 3: "", 6: "", 12: "" });
          setFullYearFinance({
            registration_fee: "",
            total_course_fee: "",
            number_of_installments: "",
            installment_amounts: [],
            installment_due_dates: [],
          });
          setOnlineInstallmentDueDates({ 1: [], 3: [], 6: [], 12: [] });
          setInPersonInstallmentDueDates({ 1: [], 3: [], 6: [], 12: [] });
          setModeLocationPlans({});
          setExistingImage(null);
        }
      } else {
        toast.error(isEditMode ? "Failed to update course, please try again." : "Failed to create course, please try again.");
      }
    } catch (err) {
      console.error("Course save error:", err.response || err);
      
      // Handle validation errors
      if (err.response?.status === 422) {
        const errors = err.response.data.errors;
        if (errors) {
          Object.entries(errors).forEach(([field, messages]) => {
            const message = Array.isArray(messages) ? messages[0] : messages;
            toast.error(`${field}: ${message}`);
          });
        } else {
          toast.error(err.response.data.message || "Validation error occurred");
        }
      } else if (err.response?.status === 404) {
        toast.error("Course not found");
        if (isEditMode) {
          navigate("/admin/course-list");
        }
      } else if (err.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("token");
        window.location.href = "/login";
      } else {
        toast.error(isEditMode 
          ? "Failed to update course, please check your input and try again."
          : "Failed to create course, please check your input and try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const renderSelectChips = (list, selected, onDelete) => (
    <Box mt={1}>
      {selected.map((id) => (
        <Chip
          key={id}
          label={list.find((i) => i.id === id)?.name || id}
          onDelete={() => onDelete(id)}
          sx={{ mr: 1, mt: 1 }}
        />
      ))}
    </Box>
  );

  if (fetchingCourse) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 2 }, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading course data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Fancy gradient header */}
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 3,
            background:
              "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #3B2A9F 100%)",
            color: "#fff",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                background: "rgba(255,255,255,0.16)",
                borderRadius: "16px",
                p: 1.25,
                boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
              }}
            >
              <Typography sx={{ fontSize: 28 }}>📘</Typography>
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  textShadow: "0 2px 6px rgba(0,0,0,0.35)",
                }}
              >
                {isEditMode ? "Edit Course" : "Create New Course"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ opacity: 0.9, mt: 0.5 }}
              >
                {isEditMode 
                  ? "Update course details, modes, locations, features and pricing."
                  : "Configure academic details, modes, locations, features and pricing in one place."
                }
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Card container with scrollable body */}
        <Paper
          elevation={4}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            background:
              "linear-gradient(180deg, #ffffff 0%, #f9fafc 60%, #f3f4f8 100%)",
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
            border: "1px solid rgba(148, 163, 184, 0.25)",
          }}
        >
          <Box
            sx={{
              maxHeight: { xs: "none", md: "calc(100vh - 210px)" },
              overflowY: { xs: "visible", md: "auto" },
              p: { xs: 2, md: 3 },
              pb: { xs: 2, md: 12 },
              scrollPaddingBottom: { xs: 24, md: 120 },
              "&::-webkit-scrollbar": {
                width: 8,
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(148,163,184,0.15)",
                borderRadius: 8,
              },
              "&::-webkit-scrollbar-thumb": {
                background:
                  "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
                borderRadius: 8,
              },
            }}
          >
            <Grid container spacing={2}>
              {/* Course Name */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Course Name"
                  {...register("name", {
                    required: "Course Name is required",
                    maxLength: {
                      value: 100,
                      message:
                        "Course name must be less than 100 characters",
                    },
                  })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>

              {/* Academic Year */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="acdemic_year_id"
                  control={control}
                  rules={{ required: "Academic Year is required" }}
                  render={({ field }) => (
                <TextField
                  select
                  fullWidth
                  label="Academic Year"
                      {...field}
                      value={field.value || ""}
                  error={!!errors.acdemic_year_id}
                  helperText={errors.acdemic_year_id?.message}
                >
                  {academicYears.map((y) => (
                    <MenuItem key={y.id} value={y.id}>
                      {y.start_end_year}
                    </MenuItem>
                  ))}
                </TextField>
                  )}
                />
              </Grid>

              {/* School year, total seats, start/end dates — one row on md+ */}
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name="school_year_id"
                  control={control}
                  rules={{ required: "School year is required" }}
                  render={({ field }) => (
                    <TextField
                      select
                      fullWidth
                      label="School year"
                      {...field}
                      value={field.value ?? ""}
                      error={!!errors.school_year_id}
                      helperText={errors.school_year_id?.message}
                    >
                      {schoolYears.map((y) => (
                        <MenuItem key={y.id} value={y.id}>
                          {y.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name="total_seats"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Total seats is required" }}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      type="number"
                      label="Total seats"
                      {...field}
                      value={field.value ?? ""}
                      inputProps={{ min: 1, max: 100000 }}
                      error={!!errors.total_seats}
                      helperText={errors.total_seats?.message}
                    />
                  )}
                />
              </Grid>

              {/* Full academic year (53 weeks) payment — gated by SHOW_FULL_ACADEMIC_YEAR_FINANCE */}
              {SHOW_FULL_ACADEMIC_YEAR_FINANCE && isEditMode ? (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Full academic year (53 weeks) payment (optional)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Use this when the course runs for the full academic year (no month-plan pricing), but still needs registration fee and installments.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="full_year_registration_fee"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            fullWidth
                            type="number"
                            size="small"
                            label="Registration fee (£)"
                            {...field}
                            value={field.value ?? ""}
                            inputProps={{ step: "0.01", min: "0" }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="full_year_total_course_fee"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            fullWidth
                            type="number"
                            size="small"
                            label="Total course fee (£)"
                            {...field}
                            value={field.value ?? ""}
                            inputProps={{ step: "0.01", min: "0" }}
                            onChange={(e) => {
                              field.onChange(e);
                              setFullYearFinance((prev) => ({ ...prev, total_course_fee: e.target.value }));
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="full_year_number_of_installments"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            fullWidth
                            type="number"
                            size="small"
                            label="No. of installments"
                            {...field}
                            value={field.value ?? ""}
                            inputProps={{ min: 1, max: 12 }}
                            onChange={(e) => {
                              field.onChange(e);
                              const n = parseInt(e.target.value, 10);
                              setFullYearFinance((prev) => ({
                                ...prev,
                                number_of_installments: e.target.value,
                                installment_amounts: Number.isFinite(n) && n >= 1 ? Array(n).fill("") : [],
                                installment_due_dates: Number.isFinite(n) && n >= 1 ? Array(n).fill("") : [],
                              }));
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: { xs: 0, md: 0.5 } }}
                        onClick={() => {
                          const n = parseInt(fullYearFinance.number_of_installments, 10);
                          const total = parseFloat(fullYearFinance.total_course_fee) || 0;
                          if (!Number.isFinite(n) || n < 1 || total <= 0) return;
                          const per = (total / n).toFixed(2);
                          const arr = Array(n)
                            .fill(per)
                            .map((v, i) => (i === n - 1 ? (total - (n - 1) * parseFloat(per)).toFixed(2) : v));
                          setFullYearFinance((prev) => ({ ...prev, installment_amounts: arr }));
                        }}
                      >
                        Split equally
                      </Button>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Controller
                        name="full_year_reg_opening_date"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            fullWidth
                            type="date"
                            size="small"
                            label="Registration start date"
                            InputLabelProps={{ shrink: true }}
                            {...field}
                            value={field.value ?? ""}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Controller
                        name="full_year_reg_closing_date"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            fullWidth
                            type="date"
                            size="small"
                            label="Registration end date"
                            InputLabelProps={{ shrink: true }}
                            {...field}
                            value={field.value ?? ""}
                          />
                        )}
                      />
                    </Grid>

                    {Array.isArray(fullYearFinance.installment_amounts) &&
                      fullYearFinance.installment_amounts.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Installment amounts (£)
                          </Typography>
                          <Grid container spacing={2}>
                            {fullYearFinance.installment_amounts.map((v, idx) => (
                              <Grid item xs={12} sm={6} md={3} key={idx}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  size="small"
                                  label={`Installment ${idx + 1} (£)`}
                                  value={v ?? ""}
                                  inputProps={{ step: "0.01", min: "0" }}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setFullYearFinance((prev) => {
                                      const next = [...(prev.installment_amounts || [])];
                                      next[idx] = val;
                                      return { ...prev, installment_amounts: next };
                                    });
                                  }}
                                  sx={{ mb: 1 }}
                                />
                                <TextField
                                  fullWidth
                                  type="date"
                                  size="small"
                                  label="Due date"
                                  InputLabelProps={{ shrink: true }}
                                  value={fullYearFinance.installment_due_dates?.[idx] ?? ""}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setFullYearFinance((prev) => {
                                      const next = [...(prev.installment_due_dates || [])];
                                      while (next.length < fullYearFinance.installment_amounts.length) next.push("");
                                      next[idx] = val;
                                      return { ...prev, installment_due_dates: next };
                                    });
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      )}
                  </Grid>
                </Paper>
              </Grid>
              ) : null}

              <Grid item xs={12} md={6}>
                <Controller
                  name="is_new"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                          color="primary"
                        />
                      }
                      label="Mark as new (shows “New” on website course tiles)"
                    />
                  )}
                />
              </Grid>

              {/* Modes */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.type_of_modes}>
                  <InputLabel>Modes</InputLabel>
                  <Controller
                    name="type_of_modes"
                    control={control}
                    defaultValue={[]}
                    rules={{ required: "Please select at least one mode" }}
                    render={({ field }) => (
                      <Select
                        multiple
                        input={<OutlinedInput label="Modes" />}
                        value={field.value || []}
                        onChange={field.onChange}
                        renderValue={(selected) => (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            {selected.map((value) => (
                              <Chip
                                key={value}
                                label={getModeName(value)}
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {modes.map((m) => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.type_of_modes && (
                    <Typography variant="caption" color="error">
                      {errors.type_of_modes.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Subjects */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Select Subject"
                  value=""
                  onChange={(e) => handleAddSubject(e.target.value)}
                >
                  {subjectsList.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>
                {renderSelectChips(
                  subjectsList,
                  subjects,
                  (id) => {
                    setSubjects(subjects.filter((s) => s !== id));
                  }
                )}
              </Grid>

              {/* Locations */}
              <Grid item xs={12} md={6}>
                {hasOnlineModeSelected && !hasInPersonModeSelected ? (
                  <TextField
                    fullWidth
                    label="Location"
                    value="Online (auto selected)"
                    InputProps={{ readOnly: true }}
                    helperText="Online mode always uses Online location."
                  />
                ) : (
                  <>
                    <TextField
                      select
                      fullWidth
                      label="Select Location"
                      value=""
                      onChange={(e) => handleAddLocation(e.target.value)}
                    >
                      {locations.map((l) => (
                        <MenuItem key={l.id} value={l.id}>
                          {l.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    {renderSelectChips(
                      locations,
                      locationsSelected,
                      (id) =>
                        setLocationsSelected(
                          locationsSelected.filter((l) => l !== id)
                        )
                    )}
                  </>
                )}
              </Grid>

              {/* One row per mode × location × subject */}
              {false && (data.type_of_modes || []).length > 0 &&
                locationsSelected.length > 0 &&
                subjects.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Class schedule
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Set weekday, start time, and duration separately for each mode, location, and subject.
                    </Typography>
                    <Grid container spacing={2}>
                      {(data.type_of_modes || []).flatMap((midRaw) =>
                        locationsSelected.flatMap((lidRaw) =>
                          subjects.map((sid) => {
                            const mid = Number(midRaw);
                            const lid = Number(lidRaw);
                            const k = sessionScheduleKey(mid, lid, sid);
                            const sched = classSessionSchedule[k] || {
                              held_time: "",
                              duration_minutes: "",
                              held_weekday_id: "",
                            };
                            const subjectName =
                              subjectsList.find((s) => s.id === sid)?.name || `Subject ${sid}`;
                            const locName =
                              locations.find((l) => l.id === lid)?.name || `Location ${lid}`;
                            return (
                              <Grid item xs={12} sm={6} md={4} lg={3} key={k}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {getModeName(mid)} · {locName}
                                  </Typography>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                    {subjectName}
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    select
                                    size="small"
                                    label="Weekday"
                                    value={sched.held_weekday_id ?? ""}
                                    onChange={(e) =>
                                      setClassSessionSchedule((prev) => ({
                                        ...prev,
                                        [k]: {
                                          ...(prev[k] || {}),
                                          held_weekday_id: e.target.value,
                                        },
                                      }))
                                    }
                                    sx={{ mb: 1.5 }}
                                  >
                                    <MenuItem value="">
                                      <em>Select weekday</em>
                                    </MenuItem>
                                    {weekDays.map((d) => (
                                      <MenuItem key={d.id} value={d.id}>
                                        {d.name}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <TextField
                                    fullWidth
                                    type="time"
                                    size="small"
                                    label="Time"
                                    InputLabelProps={{ shrink: true }}
                                    value={sched.held_time || ""}
                                    onChange={(e) =>
                                      setClassSessionSchedule((prev) => ({
                                        ...prev,
                                        [k]: { ...(prev[k] || {}), held_time: e.target.value },
                                      }))
                                    }
                                    sx={{ mb: 1.5 }}
                                  />
                                  <TextField
                                    fullWidth
                                    type="number"
                                    size="small"
                                    label="Duration (minutes)"
                                    value={sched.duration_minutes || ""}
                                    onChange={(e) =>
                                      setClassSessionSchedule((prev) => ({
                                        ...prev,
                                        [k]: { ...(prev[k] || {}), duration_minutes: e.target.value },
                                      }))
                                    }
                                    inputProps={{ min: 1, max: 600 }}
                                  />
                                </Paper>
                              </Grid>
                            );
                          })
                        )
                      )}
                    </Grid>
                  </Grid>
                )}

              {/* Features */}
              <Grid item xs={12} md={6}>
                <Box display="flex">
                  <TextField
                    fullWidth
                    label="Add Feature"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    helperText="Minimum 50 characters"
                  />
                  <IconButton onClick={handleAddFeature}>
                    <AddIcon />
                  </IconButton>
                </Box>
                {features.map((f, i) => (
                  <Chip
                    key={i}
                    label={
                      f.length > 30 ? `${f.substring(0, 30)}...` : f
                    }
                    onDelete={() =>
                      setFeatures(
                        features.filter((_, idx) => idx !== i)
                      )
                    }
                    sx={{ mr: 1, mt: 1 }}
                  />
                ))}
              </Grid>

              {/* Online Features */}
              {isModeSelected("online") && (
                <Grid item xs={12} md={6}>
                  <Box display="flex">
                    <TextField
                      fullWidth
                      label="Add Online Feature"
                      value={onlineFeatureInput}
                      onChange={(e) =>
                        setOnlineFeatureInput(e.target.value)
                      }
                      helperText="Minimum 50 characters"
                    />
                    <IconButton onClick={handleAddOnlineFeature}>
                      <AddIcon />
                    </IconButton>
                  </Box>
                  {onlineFeatures.map((f, i) => (
                    <Chip
                      key={i}
                      label={
                        f.length > 30
                          ? `${f.substring(0, 30)}...`
                          : f
                      }
                      onDelete={() =>
                        setOnlineFeatures(
                          onlineFeatures.filter((_, idx) => idx !== i)
                        )
                      }
                      sx={{ mr: 1, mt: 1 }}
                    />
                  ))}
                </Grid>
              )}

              {/* In-Person Features */}
              {isModeSelected("person") && (
                <Grid item xs={12} md={6}>
                  <Box display="flex">
                    <TextField
                      fullWidth
                      label="Add In-Person Feature"
                      value={inPersonFeatureInput}
                      onChange={(e) =>
                        setInPersonFeatureInput(e.target.value)
                      }
                      helperText="Minimum 50 characters"
                    />
                    <IconButton onClick={handleAddInPersonFeature}>
                      <AddIcon />
                    </IconButton>
                  </Box>
                  {inPersonFeatures.map((f, i) => (
                    <Chip
                      key={i}
                      label={
                        f.length > 30
                          ? `${f.substring(0, 30)}...`
                          : f
                      }
                      onDelete={() =>
                        setInPersonFeatures(
                          inPersonFeatures.filter((_, idx) => idx !== i)
                        )
                      }
                      sx={{ mr: 1, mt: 1 }}
                    />
                  ))}
                </Grid>
              )}

              {/* Legacy online course plans (kept commented/disabled for migration) */}
              {SHOW_COURSE_PLANS && isModeSelected("online") && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Online course plans
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    For each plan set registration fee, total course fee, and number of installments. Set the amount for each installment (or use Split equally).
                  </Typography>
                  <Grid container spacing={2}>
                    {subscriptionPlans.map((plan) => {
                      const courseFee = onlineAmounts[plan];
                      const numInst = onlineNumberOfInstallments[plan];
                      const num = Number.isInteger(parseInt(numInst, 10)) && parseInt(numInst, 10) >= 1 ? parseInt(numInst, 10) : 0;
                      const instAmounts = onlineInstallmentAmounts[plan] || [];
                      const equalSplitForPlan = () => {
                        const total = parseFloat(courseFee) || 0;
                        if (num < 1 || total <= 0) return [];
                        const per = (total / num).toFixed(2);
                        return Array(num)
                          .fill(per)
                          .map((v, i) => (i === num - 1 ? (total - (num - 1) * parseFloat(per)).toFixed(2) : v));
                      };
                      const handleOnlineNumInstallmentsChange = (e) => {
                        const val = e.target.value;
                        setOnlineNumberOfInstallments((prev) => ({ ...prev, [plan]: val }));
                        const n = parseInt(val, 10);
                        if (n >= 1 && courseFee) {
                          const total = parseFloat(courseFee) || 0;
                          const per = total > 0 ? (total / n).toFixed(2) : "0";
                          const arr = Array(n).fill(per).map((v, i) => (i === n - 1 ? (total - (n - 1) * parseFloat(per)).toFixed(2) : v));
                          setOnlineInstallmentAmounts((prev) => ({ ...prev, [plan]: arr }));
                          setOnlineInstallmentDueDates((prev) => ({ ...prev, [plan]: Array(n).fill("") }));
                        } else {
                          setOnlineInstallmentAmounts((prev) => ({ ...prev, [plan]: [] }));
                          setOnlineInstallmentDueDates((prev) => ({ ...prev, [plan]: [] }));
                        }
                      };
                      const sumInst = instAmounts.length === num ? instAmounts.reduce((s, v) => s + (parseFloat(v) || 0), 0) : 0;
                      const totalNum = parseFloat(courseFee) || 0;
                      const sumError = num > 0 && instAmounts.length === num && Math.abs(sumInst - totalNum) > 0.01;
                      return (
                        <Grid item xs={12} md={6} lg={3} key={plan}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              {plan} Month(s) plan
                            </Typography>
                            <TextField
                              fullWidth
                              type="date"
                              size="small"
                              label="Plan start date"
                              InputLabelProps={{ shrink: true }}
                              value={planStartDates[plan] ?? ""}
                              onChange={(e) =>
                                setPlanStartDates((prev) => ({ ...prev, [plan]: e.target.value }))
                              }
                              sx={{ mb: 1.5 }}
                            />
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              label="Registration fee (£)"
                              value={onlineRegistrationFees[plan] ?? ""}
                              onChange={(e) =>
                                setOnlineRegistrationFees((prev) => ({ ...prev, [plan]: e.target.value }))
                              }
                              inputProps={{ step: "0.01", min: "0" }}
                              sx={{ mb: 1.5 }}
                            />
                            <TextField
                              fullWidth
                              type="date"
                              size="small"
                              label="Registration start date"
                              InputLabelProps={{ shrink: true }}
                              value={onlineRegOpeningDates[plan] ?? ""}
                              onChange={(e) =>
                                setOnlineRegOpeningDates((prev) => ({ ...prev, [plan]: e.target.value }))
                              }
                              sx={{ mb: 1.5 }}
                            />
                            <TextField
                              fullWidth
                              type="date"
                              size="small"
                              label="Registration end date"
                              InputLabelProps={{ shrink: true }}
                              value={onlineRegClosingDates[plan] ?? ""}
                              onChange={(e) =>
                                setOnlineRegClosingDates((prev) => ({ ...prev, [plan]: e.target.value }))
                              }
                              sx={{ mb: 1.5 }}
                            />
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              label="Course fee total (£)"
                              value={onlineAmounts[plan] ?? ""}
                              onChange={(e) =>
                                setOnlineAmounts((prev) => ({ ...prev, [plan]: e.target.value }))
                              }
                              inputProps={{ step: "0.01", min: "0" }}
                              error={onlineAmounts[plan] && parseFloat(onlineAmounts[plan]) <= 0}
                              helperText={onlineAmounts[plan] && parseFloat(onlineAmounts[plan]) <= 0 ? "Must be > 0" : ""}
                              sx={{ mb: 1.5 }}
                            />
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              label="Number of installments"
                              value={onlineNumberOfInstallments[plan] ?? ""}
                              onChange={handleOnlineNumInstallmentsChange}
                              inputProps={{ min: 1, max: 12 }}
                              sx={{ mb: 1 }}
                            />
                            {num >= 1 && (
                              <>
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                  Amount per installment (£)
                                </Typography>
                                {Array.from({ length: num }, (_, i) => (
                                  <React.Fragment key={i}>
                                    <TextField
                                      type="number"
                                      fullWidth
                                      size="small"
                                      label={`Installment ${i + 1} (£)`}
                                      value={instAmounts[i] ?? ""}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setOnlineInstallmentAmounts((prev) => {
                                          const arr = [...(prev[plan] || [])];
                                          while (arr.length < num) arr.push("");
                                          arr[i] = v;
                                          return { ...prev, [plan]: arr };
                                        });
                                      }}
                                      inputProps={{ step: "0.01", min: "0" }}
                                      sx={{ mb: 1 }}
                                    />
                                    <TextField
                                      type="date"
                                      fullWidth
                                      size="small"
                                      label="Due date"
                                      InputLabelProps={{ shrink: true }}
                                      value={onlineInstallmentDueDates[plan]?.[i] ?? ""}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setOnlineInstallmentDueDates((prev) => {
                                          const arr = [...(prev[plan] || [])];
                                          while (arr.length < num) arr.push("");
                                          arr[i] = v;
                                          return { ...prev, [plan]: arr };
                                        });
                                      }}
                                      sx={{ mb: 1.5 }}
                                    />
                                  </React.Fragment>
                                ))}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setOnlineInstallmentAmounts((prev) => ({ ...prev, [plan]: equalSplitForPlan() }))}
                                  sx={{ mt: 0.5 }}
                                >
                                  Split equally
                                </Button>
                                {sumError && (
                                  <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5 }}>
                                    Sum of installments must equal course fee total
                                  </Typography>
                                )}
                              </>
                            )}
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              )}

              {/* Legacy in-person course plans (kept commented/disabled for migration) */}
              {SHOW_COURSE_PLANS && isModeSelected("person") && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    In-person course plans
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    For each plan set registration fee, total course fee, and number of installments. Set the amount for each installment (or use Split equally).
                  </Typography>
                  <Grid container spacing={2}>
                    {subscriptionPlans.map((plan) => {
                      const courseFee = inPersonAmounts[plan];
                      const numInst = inPersonNumberOfInstallments[plan];
                      const num = Number.isInteger(parseInt(numInst, 10)) && parseInt(numInst, 10) >= 1 ? parseInt(numInst, 10) : 0;
                      const instAmounts = inPersonInstallmentAmounts[plan] || [];
                      const equalSplitForPlanInPerson = () => {
                        const total = parseFloat(courseFee) || 0;
                        if (num < 1 || total <= 0) return [];
                        const per = (total / num).toFixed(2);
                        return Array(num)
                          .fill(per)
                          .map((v, i) => (i === num - 1 ? (total - (num - 1) * parseFloat(per)).toFixed(2) : v));
                      };
                      const handleInPersonNumInstallmentsChange = (e) => {
                        const val = e.target.value;
                        setInPersonNumberOfInstallments((prev) => ({ ...prev, [plan]: val }));
                        const n = parseInt(val, 10);
                        if (n >= 1 && courseFee) {
                          const total = parseFloat(courseFee) || 0;
                          const per = total > 0 ? (total / n).toFixed(2) : "0";
                          const arr = Array(n).fill(per).map((v, i) => (i === n - 1 ? (total - (n - 1) * parseFloat(per)).toFixed(2) : v));
                          setInPersonInstallmentAmounts((prev) => ({ ...prev, [plan]: arr }));
                        } else {
                          setInPersonInstallmentAmounts((prev) => ({ ...prev, [plan]: [] }));
                        }
                      };
                      const sumInst = instAmounts.length === num ? instAmounts.reduce((s, v) => s + (parseFloat(v) || 0), 0) : 0;
                      const totalNum = parseFloat(courseFee) || 0;
                      const sumError = num > 0 && instAmounts.length === num && Math.abs(sumInst - totalNum) > 0.01;
                      return (
                        <Grid item xs={12} md={6} lg={3} key={plan}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              {plan} Month(s) plan
                            </Typography>
                            <TextField
                              fullWidth
                              type="date"
                              size="small"
                              label="Plan start date"
                              InputLabelProps={{ shrink: true }}
                              value={planStartDates[plan] ?? ""}
                              onChange={(e) =>
                                setPlanStartDates((prev) => ({ ...prev, [plan]: e.target.value }))
                              }
                              sx={{ mb: 1.5 }}
                            />
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              label="Registration fee (£)"
                              value={inPersonRegistrationFees[plan] ?? ""}
                              onChange={(e) =>
                                setInPersonRegistrationFees((prev) => ({ ...prev, [plan]: e.target.value }))
                              }
                              inputProps={{ step: "0.01", min: "0" }}
                              sx={{ mb: 1.5 }}
                            />
                            <TextField
                              fullWidth
                              type="date"
                              size="small"
                              label="Registration start date"
                              InputLabelProps={{ shrink: true }}
                              value={inPersonRegOpeningDates[plan] ?? ""}
                              onChange={(e) =>
                                setInPersonRegOpeningDates((prev) => ({ ...prev, [plan]: e.target.value }))
                              }
                              sx={{ mb: 1.5 }}
                            />
                            <TextField
                              fullWidth
                              type="date"
                              size="small"
                              label="Registration end date"
                              InputLabelProps={{ shrink: true }}
                              value={inPersonRegClosingDates[plan] ?? ""}
                              onChange={(e) =>
                                setInPersonRegClosingDates((prev) => ({ ...prev, [plan]: e.target.value }))
                              }
                              sx={{ mb: 1.5 }}
                            />
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              label="Course fee total (£)"
                              value={inPersonAmounts[plan] ?? ""}
                              onChange={(e) =>
                                setInPersonAmounts((prev) => ({ ...prev, [plan]: e.target.value }))
                              }
                              inputProps={{ step: "0.01", min: "0" }}
                              error={inPersonAmounts[plan] && parseFloat(inPersonAmounts[plan]) <= 0}
                              helperText={inPersonAmounts[plan] && parseFloat(inPersonAmounts[plan]) <= 0 ? "Must be > 0" : ""}
                              sx={{ mb: 1.5 }}
                            />
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              label="Number of installments"
                              value={inPersonNumberOfInstallments[plan] ?? ""}
                              onChange={handleInPersonNumInstallmentsChange}
                              inputProps={{ min: 1, max: 12 }}
                              sx={{ mb: 1 }}
                            />
                            {num >= 1 && (
                              <>
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                  Amount per installment (£)
                                </Typography>
                                {Array.from({ length: num }, (_, i) => (
                                  <React.Fragment key={i}>
                                    <TextField
                                      type="number"
                                      fullWidth
                                      size="small"
                                      label={`Installment ${i + 1} (£)`}
                                      value={instAmounts[i] ?? ""}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setInPersonInstallmentAmounts((prev) => {
                                          const arr = [...(prev[plan] || [])];
                                          while (arr.length < num) arr.push("");
                                          arr[i] = v;
                                          return { ...prev, [plan]: arr };
                                        });
                                      }}
                                      inputProps={{ step: "0.01", min: "0" }}
                                      sx={{ mb: 1 }}
                                    />
                                    <TextField
                                      type="date"
                                      fullWidth
                                      size="small"
                                      label="Due date"
                                      InputLabelProps={{ shrink: true }}
                                      value={inPersonInstallmentDueDates[plan]?.[i] ?? ""}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setInPersonInstallmentDueDates((prev) => {
                                          const arr = [...(prev[plan] || [])];
                                          while (arr.length < num) arr.push("");
                                          arr[i] = v;
                                          return { ...prev, [plan]: arr };
                                        });
                                      }}
                                      sx={{ mb: 1.5 }}
                                    />
                                  </React.Fragment>
                                ))}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setInPersonInstallmentAmounts((prev) => ({ ...prev, [plan]: equalSplitForPlanInPerson() }))}
                                  sx={{ mt: 0.5 }}
                                >
                                  Split equally
                                </Button>
                                {sumError && (
                                  <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5 }}>
                                    Sum of installments must equal course fee total
                                  </Typography>
                                )}
                              </>
                            )}
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Groups by mode and location
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add one or more groups per mode and location. Set a group name, max seats, dates, fees, and subject schedule.
                </Typography>
                {selectedModeIds.length < 1 || effectiveLocationIds.length < 1 ? (
                  <Typography variant="body2" color="text.secondary">
                    Select at least one mode and one location to manage plans.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {selectedModeIds.flatMap((mid) =>
                      effectiveLocationIds.map((lid) => {
                        if (!isVisibleModeLocationPlan(mid, lid)) return null;
                        const key = modeLocationPlanKey(mid, lid);
                        const modeName = getModeName(mid) || `Mode #${mid}`;
                        const locationName =
                          locations.find((l) => Number(l.id) === Number(lid))?.name || `Location #${lid}`;
                        const plans = Array.isArray(modeLocationPlans[key])
                          ? modeLocationPlans[key]
                          : modeLocationPlans[key]
                          ? [modeLocationPlans[key]]
                          : [createEmptyModeLocationPlan()];

                        const setPlans = (updater) => {
                          setModeLocationPlans((prev) => {
                            const current = Array.isArray(prev[key])
                              ? prev[key]
                              : prev[key]
                              ? [prev[key]]
                              : [createEmptyModeLocationPlan()];
                            return { ...prev, [key]: updater(current) };
                          });
                        };

                        return (
                          <Grid item xs={12} key={key}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                                <Typography variant="subtitle2" color="primary">
                                  {modeName} - {locationName}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setPlans((current) => [...current, createEmptyModeLocationPlan()])}
                                >
                                  Add group
                                </Button>
                              </Box>
                              <Grid container spacing={2}>
                                {plans.map((plan, planIndex) => {
                                  const num = Number.isInteger(parseInt(plan.number_of_installments, 10))
                                    ? parseInt(plan.number_of_installments, 10)
                                    : 0;
                                  const instAmounts = Array.isArray(plan.installment_amounts)
                                    ? plan.installment_amounts
                                    : [];
                                  const sumInst =
                                    num > 0 && instAmounts.length === num
                                      ? instAmounts.reduce((s, v) => s + (parseFloat(v) || 0), 0)
                                      : 0;
                                  const totalNum = parseFloat(plan.total_course_fee) || 0;
                                  const durationDays =
                                    plan.plan_start_date && plan.plan_end_date
                                      ? Math.floor(
                                          (new Date(plan.plan_end_date) - new Date(plan.plan_start_date)) /
                                            (1000 * 60 * 60 * 24)
                                        ) + 1
                                      : null;
                                  const durationText =
                                    !Number.isFinite(durationDays) || durationDays <= 0
                                      ? "—"
                                      : durationDays > 30
                                      ? `${Math.floor(durationDays / 7)} week(s)${
                                          durationDays % 7 ? ` ${durationDays % 7} day(s)` : ""
                                        }`
                                      : `${durationDays} day(s)`;

                                  const setPlan = (updater) =>
                                    setPlans((current) =>
                                      current.map((p, idx) => (idx === planIndex ? updater(p) : p))
                                    );

                                  return (
                                    <Grid item xs={12} key={`${key}-${planIndex}`}>
                                      <Paper variant="outlined" sx={{ p: 2, borderStyle: "dashed" }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                          <Typography variant="subtitle2">Group {planIndex + 1}</Typography>
                                          {plans.length > 1 && (
                                            <Button
                                              size="small"
                                              color="error"
                                              onClick={() =>
                                                setPlans((current) =>
                                                  current.filter((_, idx) => idx !== planIndex)
                                                )
                                              }
                                            >
                                              Remove
                                            </Button>
                                          )}
                                        </Box>
                                        <Grid container spacing={1.5} sx={{ mb: 1 }}>
                                          <Grid item xs={12} md={6}>
                                            <TextField
                                              fullWidth
                                              size="small"
                                              label="Group name"
                                              required
                                              value={plan.plan_name || ""}
                                              onChange={(e) => setPlan((c) => ({ ...c, plan_name: e.target.value }))}
                                            />
                                          </Grid>
                                          <Grid item xs={12} md={6}>
                                            <TextField
                                              type="number"
                                              fullWidth
                                              size="small"
                                              label="Max seats for this group"
                                              required
                                              value={plan.max_seats || ""}
                                              onChange={(e) => setPlan((c) => ({ ...c, max_seats: e.target.value }))}
                                              inputProps={{ min: 1 }}
                                            />
                                          </Grid>
                                          {(plan.enrolled_count != null || plan.available_seats != null) && (
                                            <Grid item xs={12}>
                                              <Typography variant="caption" color="text.secondary">
                                                Enrolled: {plan.enrolled_count ?? 0}
                                                {plan.available_seats != null ? ` · Available: ${plan.available_seats}` : ""}
                                              </Typography>
                                            </Grid>
                                          )}
                                        </Grid>
                                        <Grid container spacing={1.5} sx={{ mb: 1 }}>
                                          <Grid item xs={12} md={4}>
                                            <TextField fullWidth type="date" size="small" label="Plan start date" InputLabelProps={{ shrink: true }} value={plan.plan_start_date || ""} onChange={(e) => setPlan((c) => ({ ...c, plan_start_date: e.target.value }))} />
                                          </Grid>
                                          <Grid item xs={12} md={4}>
                                            <TextField fullWidth type="date" size="small" label="Plan end date" InputLabelProps={{ shrink: true }} value={plan.plan_end_date || ""} onChange={(e) => setPlan((c) => ({ ...c, plan_end_date: e.target.value }))} />
                                          </Grid>
                                          <Grid item xs={12} md={4}>
                                            <TextField fullWidth size="small" label="Course duration" value={durationText} InputProps={{ readOnly: true }} helperText="Auto from start/end" />
                                          </Grid>
                                        </Grid>

                                        <Grid container spacing={1.5} sx={{ mb: 1 }}>
                                          <Grid item xs={12} md={4}>
                                            <TextField fullWidth type="date" size="small" label="Registration start date" InputLabelProps={{ shrink: true }} value={plan.reg_opening_date || ""} onChange={(e) => setPlan((c) => ({ ...c, reg_opening_date: e.target.value }))} />
                                          </Grid>
                                          <Grid item xs={12} md={4}>
                                            <TextField fullWidth type="date" size="small" label="Registration end date" InputLabelProps={{ shrink: true }} value={plan.reg_closing_date || ""} onChange={(e) => setPlan((c) => ({ ...c, reg_closing_date: e.target.value }))} />
                                          </Grid>
                                          <Grid item xs={12} md={4}>
                                            <TextField type="number" fullWidth size="small" label="Registration fee (£)" value={plan.registration_fee || ""} onChange={(e) => setPlan((c) => ({ ...c, registration_fee: e.target.value }))} inputProps={{ step: "0.01", min: "0" }} />
                                          </Grid>
                                        </Grid>

                                        <Grid container spacing={1.5} sx={{ mb: 1 }}>
                                          <Grid item xs={12} md={6}>
                                            <TextField type="number" fullWidth size="small" label="Course fee total (£)" value={plan.total_course_fee || ""} onChange={(e) => setPlan((c) => ({ ...c, total_course_fee: e.target.value }))} inputProps={{ step: "0.01", min: "0" }} />
                                          </Grid>
                                          <Grid item xs={12} md={6}>
                                            <TextField
                                              type="number"
                                              fullWidth
                                              size="small"
                                              label="Number of installments"
                                              value={plan.number_of_installments || ""}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                const n = parseInt(val, 10);
                                                setPlan((c) => {
                                                  if (!Number.isFinite(n) || n < 1) return { ...c, number_of_installments: val, installment_amounts: [], installment_due_dates: [] };
                                                  const total = parseFloat(c.total_course_fee) || 0;
                                                  const per = total > 0 ? (total / n).toFixed(2) : "0";
                                                  const amounts = Array(n).fill(per).map((v, i) => (i === n - 1 ? (total - (n - 1) * parseFloat(per)).toFixed(2) : v));
                                                  return { ...c, number_of_installments: val, installment_amounts: amounts, installment_due_dates: Array(n).fill("") };
                                                });
                                              }}
                                              inputProps={{ min: 1, max: 24 }}
                                            />
                                          </Grid>
                                        </Grid>
                                        {num >= 1 && (
                                          <>
                                            <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1, mb: 1 }}>
                                              {Array.from({ length: num }, (_, i) => (
                                                <Paper key={`${key}-${planIndex}-${i}`} variant="outlined" sx={{ minWidth: 220, p: 1 }}>
                                                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                    Installment {i + 1}
                                                  </Typography>
                                                  <TextField type="number" fullWidth size="small" label="Amount (£)" value={plan.installment_amounts?.[i] ?? ""} onChange={(e) => setPlan((c) => { const arr = [...(c.installment_amounts || [])]; while (arr.length < num) arr.push(""); arr[i] = e.target.value; return { ...c, installment_amounts: arr }; })} inputProps={{ step: "0.01", min: "0" }} sx={{ mt: 1, mb: 1 }} />
                                                  <TextField type="date" fullWidth size="small" label="Due date" InputLabelProps={{ shrink: true }} value={plan.installment_due_dates?.[i] ?? ""} onChange={(e) => setPlan((c) => { const arr = [...(c.installment_due_dates || [])]; while (arr.length < num) arr.push(""); arr[i] = e.target.value; return { ...c, installment_due_dates: arr }; })} />
                                                </Paper>
                                              ))}
                                            </Box>
                                            <Typography variant="caption" display="block" color="text.secondary">
                                              Enter installment amounts as needed (equal or different).
                                            </Typography>
                                          </>
                                        )}
                                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Subject schedule for this plan</Typography>
                                        <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 1 }}>
                                          {subjects.map((sidRaw) => {
                                            const sid = Number(sidRaw);
                                            if (!Number.isFinite(sid) || sid < 1) return null;
                                            const subjectName = subjectsList.find((s) => Number(s.id) === sid)?.name || `Subject ${sid}`;
                                            const sched = plan.subject_sessions?.[sid] || plan.subject_sessions?.[String(sid)] || { held_time: "", duration_minutes: "", held_weekday_id: "" };
                                            return (
                                              <Paper key={`${key}-${planIndex}-${sid}`} variant="outlined" sx={{ p: 1.5, minWidth: 320 }}>
                                                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{subjectName}</Typography>
                                                  <TextField fullWidth select size="small" label="Weekday" value={sched.held_weekday_id ?? ""} onChange={(e) => setPlan((c) => ({ ...c, subject_sessions: { ...(c.subject_sessions || {}), [sid]: { ...((c.subject_sessions || {})[sid] || {}), held_weekday_id: e.target.value } } }))} sx={{ mt: 1, mb: 1 }}>
                                                    <MenuItem value=""><em>Select weekday</em></MenuItem>
                                                    {weekDays.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                                                  </TextField>
                                                  <TextField fullWidth type="time" size="small" label="Time" InputLabelProps={{ shrink: true }} value={sched.held_time || ""} onChange={(e) => setPlan((c) => ({ ...c, subject_sessions: { ...(c.subject_sessions || {}), [sid]: { ...((c.subject_sessions || {})[sid] || {}), held_time: e.target.value } } }))} sx={{ mb: 1 }} />
                                                  <TextField fullWidth type="number" size="small" label="Duration (minutes)" value={sched.duration_minutes || ""} onChange={(e) => setPlan((c) => ({ ...c, subject_sessions: { ...(c.subject_sessions || {}), [sid]: { ...((c.subject_sessions || {})[sid] || {}), duration_minutes: e.target.value } } }))} inputProps={{ min: 1, max: 600 }} />
                                                </Paper>
                                            );
                                          })}
                                        </Box>
                                      </Paper>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Paper>
                          </Grid>
                        );
                      })
                    )}
                  </Grid>
                )}
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  maxRows={8}
                  label="Description"
                  {...register("description", {
                    required: "Description is required",
                    minLength: {
                      value: 500,
                      message:
                        "Description must be at least 500 characters long",
                    },
                    maxLength: {
                      value: 10000,
                      message:
                        "Description must be less than 10000 characters",
                    },
                  })}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              </Grid>

              {/* Image Upload */}
              <Grid item xs={12}>
                <Controller
                  name="course_image"
                  control={control}
                  rules={!isEditMode ? { required: "Course image is required" } : {}}
                  render={({ field }) => (
                    <Box>
                      {existingImage && !field.value && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                            Current Image:
                          </Typography>
                          <Box
                            component="img"
                            src={existingImage}
                            alt="Current course image"
                            sx={{
                              maxWidth: "300px",
                              maxHeight: "200px",
                              borderRadius: 2,
                              border: "1px solid #e0e0e0",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            field.onChange(e.target.files[0]);
                            setExistingImage(null); // Clear existing image preview when new one selected
                          }
                        }}
                      />
                      {field.value && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                            New Image Selected:
                          </Typography>
                          <Box
                            component="img"
                            src={URL.createObjectURL(field.value)}
                            alt="Preview"
                            sx={{
                              maxWidth: "300px",
                              maxHeight: "200px",
                              borderRadius: 2,
                              border: "1px solid #e0e0e0",
                              objectFit: "cover",
                            }}
                          />
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {field.value.name}
                          </Typography>
                        </Box>
                      )}
                      {isEditMode && (
                        <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                          Leave empty to keep existing image, or upload a new one to replace it.
                        </Typography>
                      )}
                    </Box>
                  )}
                />
                {errors.course_image && (
                  <Typography color="error">
                    {errors.course_image.message}
                  </Typography>
                )}
              </Grid>

              {/* Submit */}
              <Grid
                item
                xs={12}
                sx={{
                  position: "static",
                  mt: 2,
                  pt: 2,
                  background:
                    "linear-gradient(180deg, rgba(249,250,252,0.96) 0%, rgba(241,245,249,0.98) 100%)",
                  borderTop: "1px solid rgba(148, 163, 184, 0.25)",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading || fetchingCourse}
                    sx={{
                      px: 4,
                      py: 1.2,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow:
                        "0 10px 25px rgba(37,99,235,0.4)",
                      background:
                        "linear-gradient(135deg, #6366f1 0%, #4f46e5 40%, #7c3aed 100%)",
                      "&:hover": {
                        boxShadow:
                          "0 12px 30px rgba(79,70,229,0.6)",
                        transform: "translateY(-1px)",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {loading ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={20} sx={{ color: "white" }} />
                        {isEditMode ? "Updating..." : "Creating..."}
                      </Box>
                    ) : (
                      isEditMode ? "Update Course" : "Submit"
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </form>
    </Box>
  );
}